import { Pipe, PipeTransform } from '@angular/core';
import { FlattenSeat } from '../types';
import { IShape } from '../interfaces/IShape';
import { ColorUtils } from '../../../../core/utils/ColorUtils';
import { ShapeSeatTypes } from '../enums/ShapeSeatTypes';
import { SeatStatuses } from '../enums/SeatStatuses';
import { SvgUtils } from '../utils/SvgUtils';
import { ElementTypes } from '../enums/ElementTypes';
import { ShapeSeatTypeUtils } from '../utils/ShapeSeatTypeUtils';

@Pipe({
  name: 'svgToBase64Bg',
  standalone: true,
})
export class SvgToBase64BgPipe implements PipeTransform {

  private readonly _defaultChairColor = '#A5D4FA';

  public transform(seat: FlattenSeat, shape: IShape): string {
    const color = seat.priceAreaColor ?? seat.color;

    let svg: string;
    const seatType = ShapeSeatTypeUtils.resolve(shape?.shapeSeatType);

    if (seatType && seatType !== ShapeSeatTypes.NONE) {
      const color2 = ColorUtils.adjustColor(color, 20);
      const color3 = ColorUtils.adjustColor(color, 80);

      const getNotedSvg = (): string =>
        seat.isMy ? SvgUtils.getMyNotedSeatSvg() : SvgUtils.getNotMyNotedSeatSvg();

      switch (seatType) {
        case ShapeSeatTypes.SEAT:
          if (!seat.loveSeatId) {
            switch (seat.seatStatusKey) {
              case SeatStatuses.FREE:
                svg = SvgUtils.getNormalFreeSeatSvg(color, color2, color3);
                break;
              case SeatStatuses.BLOCKED:
                svg = SvgUtils.getNormalBlockedSeatSvg();
                break;
              case SeatStatuses.RESERVED:
                svg = SvgUtils.getNormalReservedSeatSvg();
                break;
              default:
                svg = getNotedSvg();
            }
          } else {
            switch (seat.seatStatusKey) {
              case SeatStatuses.FREE:
                svg = SvgUtils.getLoveSeatInnerFreeSeatSvg(color, color2, color3);
                break;
              case SeatStatuses.BLOCKED:
                svg = SvgUtils.getLoveSeatInnerBlockedSeatSvg();
                break;
              case SeatStatuses.RESERVED:
                svg = SvgUtils.getLoveSeatInnerReservedSeatSvg();
                break;
              default:
                svg = seat.isMy
                  ? SvgUtils.getLoveSeatInnerMyNotedSvg()
                  : SvgUtils.getLoveSeatInnerNotMyNotedSvg();
            }
          }
          break;

        case ShapeSeatTypes.NORMAL_CHAIR:
          switch (seat.seatStatusKey) {
            case SeatStatuses.FREE:
              svg = this.resolveFreeChairSvg(color);
              break;
            case SeatStatuses.BLOCKED:
              svg = SvgUtils.getNormalBlockedChairSvg();
              break;
            case SeatStatuses.RESERVED:
              svg = SvgUtils.getNormalReservedChairSvg();
              break;
            default:
              svg = seat.isMy
                ? SvgUtils.getMyNotedChairSvg()
                : SvgUtils.getNotMyNotedChairSvg();
          }
          break;

        case ShapeSeatTypes.DISABLED:
          switch (seat.seatStatusKey) {
            case SeatStatuses.FREE:
              svg = SvgUtils.getDisabledFreeSeatSvg(
                color,
                color2,
                color3,
                ColorUtils.isLightColor(color) ? 'black' : 'white'
              );
              break;
            case SeatStatuses.BLOCKED:
              svg = SvgUtils.getNormalBlockedSeatSvg();
              break;
            case SeatStatuses.RESERVED:
              svg = SvgUtils.getNormalReservedSeatSvg();
              break;
            default:
              svg = getNotedSvg();
          }
          break;

        case ShapeSeatTypes.SEAT_WITH_FOOTSTOOL:
          switch (seat.seatStatusKey) {
            case SeatStatuses.FREE:
              svg = SvgUtils.getSeatWithFootstoolFreeSvg(
                color,
                color2,
                color3,
                ColorUtils.isLightColor(color) ? 'black' : 'white'
              );
              break;
            case SeatStatuses.BLOCKED:
              svg = SvgUtils.getSeatWithFootstoolBlockedSeatSvg();
              break;
            case SeatStatuses.RESERVED:
              svg = SvgUtils.getSeatWithFootstoolReservedSeatSvg();
              break;
            default:
              svg = seat.isMy
                ? SvgUtils.getSeatWithFootstoolMyNotedSvg()
                : SvgUtils.getSeatWithFootstoolNotMyNotedSvg();
          }
          break;

        case ShapeSeatTypes.LOVESEAT_LEFT:
          switch (seat.seatStatusKey) {
            case SeatStatuses.FREE:
              svg = SvgUtils.getLoveSeatLeftFreeSeatSvg(color, color2, color3);
              break;
            case SeatStatuses.BLOCKED:
              svg = SvgUtils.getLoveSeatLeftBlockedSeatSvg();
              break;
            case SeatStatuses.RESERVED:
              svg = SvgUtils.getLoveSeatLeftReservedSeatSvg();
              break;
            default:
              svg = seat.isMy
                ? SvgUtils.getLoveSeatLeftMyNotedSvg()
                : SvgUtils.getLoveSeatLeftNotMyNotedSvg();
          }
          break;

        case ShapeSeatTypes.LOVESEAT_RIGHT:
          switch (seat.seatStatusKey) {
            case SeatStatuses.FREE:
              svg = SvgUtils.getLoveSeatRightFreeSeatSvg(color, color2, color3);
              break;
            case SeatStatuses.BLOCKED:
              svg = SvgUtils.getLoveSeatRightBlockedSeatSvg();
              break;
            case SeatStatuses.RESERVED:
              svg = SvgUtils.getLoveSeatRightReservedSeatSvg();
              break;
            default:
              svg = seat.isMy
                ? SvgUtils.getLoveSeatRightMyNotedSvg()
                : SvgUtils.getLoveSeatRightNotMyNotedSvg();
          }
          break;

        case ShapeSeatTypes.WHEELCHAIR:
        case ShapeSeatTypes.CON_SEAT:
          switch (seat.seatStatusKey) {
            case SeatStatuses.FREE:
              svg = SvgUtils.getNormalFreeSeatSvg(color, color2, color3);
              break;
            case SeatStatuses.BLOCKED:
              svg = SvgUtils.getNormalBlockedSeatSvg();
              break;
            case SeatStatuses.RESERVED:
              svg = SvgUtils.getNormalReservedSeatSvg();
              break;
            default:
              svg = getNotedSvg();
          }
          break;

        default: {
          const _exhaustive: never = seatType;
          return _exhaustive;
        }
      }
    } else {
      svg = this.resolveElementSvg(shape?.shapeElementTypeKey, color);
    }

    if (!svg) {
      return '';
    }

    if (!svg) {
      return 'none';
    }

    const encoded = encodeURIComponent(svg.trim())
      .replace(/'/g, '%27')
      .replace(/"/g, '%22');

    return `url("data:image/svg+xml,${encoded}")`;
  }

  private resolveFreeChairSvg(color: string): string {
    if (!color || color.toUpperCase() === this._defaultChairColor.toUpperCase()) {
      return SvgUtils.getNormalChairSvg();
    }

    return SvgUtils.getNormalChairSvg(
      ColorUtils.adjustColor(color, 45),
      color,
      ColorUtils.adjustColor(color, -35)
    );
  }

  private resolveElementSvg(shapeElementTypeKey: string | undefined, color: string): string | undefined {
    const elementType = ShapeSeatTypeUtils.resolveElementType(shapeElementTypeKey);
    if (!elementType) {
      return undefined;
    }

    switch (elementType) {
      case ElementTypes.SCREEN:
        return SvgUtils.getScreenSvg(color);
      case ElementTypes.ROUND_TABLE:
        return SvgUtils.getRoundTableSvg(color);
      case ElementTypes.SQUARE_TABLE:
        return SvgUtils.getSquareTableSvg(color);
      case ElementTypes.ENTRANCE_EXIT:
        return SvgUtils.getEntranceExitSvg(color);
      case ElementTypes.STAIRCASE:
        return SvgUtils.getStaircaseSvg(color);
      default: {
        const _exhaustive: never = elementType;
        return _exhaustive;
      }
    }
  }
}
